<template>
  <header>
    <div class="title">
      <RocketOutlined class="logo-icon" />
      <span class="logo-text">智汇题库</span>
    </div>
    <div class="quota-box">
      <div class="usage-row">
        <span v-if="requestLimit < 0">请求次数：不限</span>
        <span v-else>剩余次数：{{ Math.max(requestLimit - requestUsed, 0) }}</span>
      </div>
    </div>
    <a-button v-if="hasLogout" size="small" class="copy-url-btn" @click="openCopyModal">
      <template #icon><CopyOutlined /></template>
      复制接口
    </a-button>
    <a-button v-if="hasLogout && canApplyRequest" size="small" class="apply-btn" @click="showApply = true">申请次数</a-button>
    <a-dropdown v-if="hasLogout" placement="bottomRight" trigger="hover">
      <div class="user-avatar">
        <a-avatar class="avatar-circle">{{ avatarText }}</a-avatar>
        <span class="avatar-name">{{ displayName }}</span>
      </div>
      <template #overlay>
        <a-menu @click="onMenuClick">
          <a-menu-item key="settings">用户设置</a-menu-item>
          <a-menu-item key="logout">退出登录</a-menu-item>
        </a-menu>
      </template>
    </a-dropdown>
    <a-modal v-if="hasLogout" v-model:visible="showApply" title="申请额外次数" @ok="submitApply" ok-text="提交" cancel-text="取消">
      <a-form :model="applyForm">
        <a-form-item label="数量">
          <a-input-number v-model:value="applyForm.amount" :min="1" :precision="0" style="width: 100%;" />
        </a-form-item>
        <a-form-item label="理由">
          <a-textarea v-model:value="applyForm.reason" :rows="3" placeholder="可选，说明用途" />
        </a-form-item>
      </a-form>
    </a-modal>
    <a-modal
      v-if="hasLogout"
      v-model:visible="showCopyModal"
      title="复制搜题接口"
      ok-text="复制"
      cancel-text="取消"
      @ok="copySearchUrl"
    >
      <a-form layout="vertical">
        <a-form-item label="客户端">
          <a-select v-model:value="selectedClient" :options="clientOptions" />
        </a-form-item>
        <a-form-item label="接口地址">
          <a-input :value="searchUrl" readonly />
        </a-form-item>
      </a-form>
    </a-modal>
  </header>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { CopyOutlined, RocketOutlined } from '@ant-design/icons-vue'
import { applyRequest, getQuota } from '@/api/TikuComponentApi'
import { hasPermission, PERMISSIONS } from '@/utils/permissions'
import { message } from 'ant-design-vue'
import { useSessionStore } from '@/store/session'
import { storeToRefs } from 'pinia'

export default defineComponent({
  name: 'PageHeader',
  props: {
    hasLogout: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const session = useSessionStore()
    const { authUserId, username, displayName, requestLimit, requestUsed, userToken } = storeToRefs(session)
    const canApplyRequest = hasPermission(PERMISSIONS.REQUEST_APPLY)
    const showApply = ref(false)
    const showCopyModal = ref(false)
    const selectedClient = ref('ocs')
    const clientOptions = [
      { label: 'OCS', value: 'ocs' },
      { label: '默认', value: 'default' },
    ]
    const applyForm = ref({
      amount: 10,
      reason: ''
    })
    const searchUrl = computed(() => {
      const origin = window.location.origin
      const params = new URLSearchParams({
        token: userToken.value || '',
        client: selectedClient.value,
      })
      return `${origin}/v1/search?${params.toString()}`
    })
    const displayNameText = computed(() => displayName.value || username.value || authUserId.value || '用户')
    const avatarText = computed(() => {
      const base = (displayName.value || username.value || authUserId.value || 'U').toString().trim()
      return base ? base[0].toUpperCase() : 'U'
    })
    return {
      authUserId,
      username,
      requestLimit,
      requestUsed,
      userToken,
      canApplyRequest,
      showApply,
      showCopyModal,
      selectedClient,
      clientOptions,
      applyForm,
      searchUrl,
      displayName: displayNameText,
      avatarText,
      session
    }
  },
  components: {
    CopyOutlined,
    RocketOutlined
  },
  methods: {
    async logoutOut() {
      const accessToken = this.session.accessToken
      this.session.clear()
      try {
        await fetch('/bff/logout', {
          method: 'POST',
          credentials: 'include',
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        })
      } catch (e) {
        // ignore network failures and still redirect to gateway login
      }
      window.location.href = '/login'
    },
    onMenuClick(info) {
      if (info?.key === 'logout') {
        this.logoutOut()
        return
      }
      if (info?.key === 'settings') {
        this.$router.push('/adapter/settings')
      }
    },
    openCopyModal() {
      if (!this.userToken) {
        message.warning('当前账号暂无 usertoken')
        return
      }
      this.showCopyModal = true
    },
    async copySearchUrl() {
      if (!this.userToken) {
        message.warning('当前账号暂无 usertoken')
        return
      }
      try {
        await copyText(this.searchUrl)
        message.success('接口地址已复制')
        this.showCopyModal = false
      } catch (e) {
        message.error('复制失败，请手动复制')
      }
    },
    pullQuota() {
      getQuota().then(res => {
        if (res.status === 200) {
          const { requestLimit, requestUsed } = res.data
          this.refreshQuota(requestLimit, requestUsed, false)
        }
      })
    },
    submitApply() {
      if (!this.applyForm.amount || this.applyForm.amount <= 0) {
        message.error('请填写申请数量')
        return
      }
      applyRequest(this.applyForm).then(res => {
        if (res.status === 200) {
          message.success('已提交申请')
          this.showApply = false
          this.pullQuota()
        } else {
          message.error(res.message || '提交失败')
        }
      })
    },
    refreshQuota(limit, used, shouldPull = true) {
      this.session.setQuota(limit, used)
      // 默认不额外拉取，避免轮询；若调用方需要以服务端为准，可显式传 shouldPull=true
      if (shouldPull) {
        this.pullQuota()
      }
    }
  },
  mounted() {
    // 仅在组件挂载时拉取一次，避免轮询造成卡顿
    this.pullQuota()
  }
})

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand('copy')
  document.body.removeChild(textarea)
  if (!copied) throw new Error('copy failed')
}
</script>

<style scoped>
header {
  width: 100%;
  height: 60px;
  background-color: #195ca3;
  border-bottom: 1px solid #e8e8e8;
  line-height: 60px;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  padding: 0 24px;
  color: #fff;
}

.title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.logo-icon {
  font-size: 24px;
  color: #a6d1ff;
}

.logo-text {
  font-size: 20px;
  font-weight: 700;
  background: linear-gradient(to right, #ffffff, #a6d1ff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  letter-spacing: 1px;
}

.quota-box {
  margin-left: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  line-height: 1.5;
}

.usage-row {
  opacity: 0.85;
  font-size: 12px;
}

.logout {
  margin-left: auto;
  cursor: pointer;
}

.apply-btn {
  margin-left: 12px;
}

.copy-url-btn {
  margin-left: 24px;
}

.user-avatar {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.avatar-circle {
  background: #1e80ff;
  font-weight: 700;
}

.avatar-name {
  font-size: 14px;
  color: #f5f5f5;
}
</style>
